import { defineRule } from 'vee-validate';

// The form rules, registered once when the app boots (imported for its side
// effect by main.ts). vee-validate keeps rules in one global registry, so a
// component registering its own copy on import would silently replace
// everyone else's — and which message a field showed would depend on which
// page happened to load last. Each rule takes the field's display name as its
// last parameter: `:rules="{ required: ['Email'] }"`.
//
// Plain JS on purpose: importing vee-validate from a .ts file makes the type
// checker parse its bundled type-fest declarations, which need a newer
// TypeScript than this project's.

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// The backend's rule for an organization's street address.
const ADDRESS_PATTERN = /^[A-Za-z0-9\s,.\-#/]+$/;
const ALPHA_PATTERN = /^[a-zA-Z]*$/;

const isBlank = (value) =>
  value == null || (Array.isArray(value) ? value.length === 0 : String(value).trim().length === 0);

defineRule('required', (value, [fieldName = 'Field'] = []) => {
  return isBlank(value) ? `${fieldName} is required` : true;
});

defineRule('email', (value) => {
  return EMAIL_PATTERN.test(String(value ?? '')) || 'Email is not valid';
});

defineRule('alpha', (value, [fieldName = 'Field'] = []) => {
  return (
    ALPHA_PATTERN.test(String(value ?? '')) ||
    `${fieldName} must contain only alphabetic characters`
  );
});

// Leaves emptiness to `required`; checks length and characters like the backend.
defineRule('address', (value) => {
  const text = String(value ?? '').trim();
  if (!text) return true;
  if (text.length < 5 || text.length > 100) return 'Address must be between 5 and 100 characters long';
  return (
    ADDRESS_PATTERN.test(text) ||
    'Address may only contain letters, numbers, spaces, commas, dots, hyphens, slashes and #'
  );
});

defineRule('min', (value, [minLength = 0, fieldName = 'Field'] = []) => {
  return String(value ?? '').length >= Number(minLength)
    ? true
    : `${fieldName} must be at least ${minLength} characters long`;
});
